import { Box, Divider, Stack, Typography } from '@mui/material'
import { EditProduct } from './ProductListView'
import { colors } from '../../../../../themes/colors'
import Counter from '../../../../../components/Counter'
import React from 'react'
import { Button } from '../../../../../components/Button'
import { ProductVariantDetails } from '../../../../products/components/ProductCard/ProductVariants'
import { useQuery } from '@tanstack/react-query'
import { Variant } from '../../../../products/utils/useVariations'
import TestIds from '../../../../../testIds'
import { ImageSlider } from '../../../../../components/ImageSlider'
import { fetchProductDetail } from '../../../../products/utils/fetchProductDetail'
import { useSelector } from 'react-redux'
import { selectCurrentFarmerAccount } from '../../../../azure-b2c/storage'
import { MediaLocationType } from '../../../../products/storage'
import { URLS } from '../../../../../util/consts'
import { Loader, LoadingBox } from '../../../../../components/Loader'
import {
  PriceStyle,
  ProductPriceDetails,
} from '../../../../products/components/ProductCard/ProductPriceDetails'
import { formatPrice } from '../../../../../util/helpers'
import { CartItem } from '../../../../cart/storage/cart.types'
import { getUpdatedVariant } from '../../../../cart/utils/getUpdatedVariant'
import { useBranch } from '../../../../../util/useBranch'

interface EditProductViewProps extends EditProduct {
  onAddProduct: (product: CartItem) => void
}

const EditProductView: React.FC<EditProductViewProps> = ({
  name,
  item_id,
  id,
  online_description,
  activePrice,
  onAddProduct,
}) => {
  const branchId = useBranch()
  const [quantity, setQuantity] = React.useState(1)
  const account = useSelector(selectCurrentFarmerAccount)
  const [product, setProduct] = React.useState<CartItem>()

  const productDetailQuery = useQuery({
    queryKey: ['fetch-product', id, account?.accountNumber, branchId],
    queryFn: async () => {
      return await fetchProductDetail(String(id), account?.accountNumber, branchId)
    },
  })

  React.useMemo(() => {
    // this should only set for first time the product is fetched
    if (productDetailQuery.isSuccess) {
      setProduct({
        ...productDetailQuery.data,
        quantity,
      })
    }
  }, [productDetailQuery.data, productDetailQuery.isSuccess])

  function handleAddProduct() {
    if (product) {
      onAddProduct({
        ...product,
        quantity,
      })
    }
  }

  function handleChangeVariant(variant: Variant) {
    const updatedVariant = getUpdatedVariant(variant, {
      ...productDetailQuery.data,
      quantity,
    } as CartItem)
    setProduct(updatedVariant)
  }

  if (productDetailQuery.isFetching) {
    return (
      <LoadingBox>
        <Loader />
      </LoadingBox>
    )
  }

  return (
    <Stack direction='column'>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={4}
        sx={{
          borderWidth: '1px',
          borderStyle: 'solid',
          borderColor: colors.primary100,
          py: 4,
          px: 1,
        }}
      >
        <Box flex={1} width={{ md: 400 }}>
          <ProductImageSlider images={productDetailQuery.data?.mediaLocations} />
        </Box>

        <Stack direction='column' gap={4} flex={1}>
          <Stack direction='column' gap={1}>
            <Typography variant='h2' sx={{ color: colors.grey800 }}>
              {name}
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: colors.grey700 }}
            >{`SKU: ${item_id}`}</Typography>
            <Divider sx={{ mt: 0.5, mb: 0.5 }} />
            <Typography
              variant='body2'
              color='grey700'
              data-testid={TestIds.feat.product.details.shortDescription}
            >
              {online_description}
            </Typography>
          </Stack>

          <ProductPriceDetails
            items={[
              {
                type: PriceStyle.Discounted,
                label: 'Your price:',
                gstLabel: 'GST excl.',
                value: activePrice ? formatPrice(activePrice?.discountedPriceExcGst) : 'N/A',
                valueWithGst: activePrice?.discountedPriceIncGst,
                testId: TestIds.feat.product.card.discountedPrice,
              },
              {
                type: PriceStyle.RRP,
                label: 'RRP:',
                gstLabel: 'GST excl.',
                value: activePrice ? formatPrice(activePrice?.basePriceExcGst) : 'N/A',
                testId: TestIds.feat.product.card.discountedPrice,
              },
            ]}
            size='Large'
            sx={{
              gap: 2,
            }}
          />

          {product ? (
            <ProductVariantDetails
              item={product}
              enableVariants
              onChangeVariant={handleChangeVariant}
            />
          ) : null}

          <Stack direction='row' gap={2}>
            <Counter
              value={quantity}
              onChange={(value) => {
                setQuantity(value)
              }}
            />
            <Button variant='contained' fullWidth onClick={handleAddProduct}>
              Add to quote
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  )
}

interface ProductImageSliderProps {
  images: MediaLocationType[] | undefined
}

const ProductImageSlider: React.FC<ProductImageSliderProps> = ({ images }) => {
  const productImages = React.useMemo(() => {
    if (images) {
      return images?.map((x) => `${URLS.imageBase}${x.uri}`)
    }
    return []
  }, [images])

  return <ImageSlider images={productImages} />
}

export default EditProductView
